import { axiosInstance } from "../../Authentication/AxiosInterceptor";

export interface ConversationFileUploadResponse {
  fileId: string;
  fileName: string;
  extension: string;
  fileSize: number;
  conversationId: string;
  uploadedAt: string;
}

export interface EvaluateConversationFile {
  fileId: string;
  fileName: string;
  extension: string;
  fileSize: number;
  conversationId: string;
  uploadedAt: string;
  uploadedBy: string;
}

function normalizeBlobUrl(data: unknown): string {
  if (typeof data === "string") {
    return data.trim().replace(/^['"]|['"]$/g, "");
  }
  if (data != null && typeof data === "object") {
    const value = Object.values(data)[0];
    if (typeof value === "string") {
      return value.trim().replace(/^['"]|['"]$/g, "");
    }
  }
  throw new Error("Unexpected response from file URL endpoint");
}

/**
 * Uploads a file inline for the Evaluate autofill flow. The file is stored as a
 * conversation file tagged with the area/criteria context so it can be listed later.
 * Omit conversationId on the first upload; the server creates one and returns it.
 */
export async function uploadEvaluateConversationFile(
  file: File,
  clientId: string,
  areaId: string,
  criteriaId: string,
  conversationId?: string
): Promise<ConversationFileUploadResponse> {
  const formData = new FormData();
  formData.append("clientId", clientId);
  formData.append("areaId", areaId);
  formData.append("criteriaId", criteriaId);
  formData.append("file", file);

  const route = conversationId
    ? `uploadConversationFile/${conversationId}`
    : "uploadConversationFile";

  const response = await axiosInstance.post<ConversationFileUploadResponse>(route, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
}

export async function getEvaluateConversationFiles(
  areaId: string,
  criteriaId: string,
  clientId: string
): Promise<EvaluateConversationFile[]> {
  const response = await axiosInstance.get<EvaluateConversationFile[]>(
    `evaluateConversationFiles/${areaId}/${criteriaId}`,
    { params: { clientId } }
  );

  const data = response.data ?? [];
  return data.map((file) =>
    normalizeEvaluateConversationFile(file as unknown as Record<string, unknown>)
  );
}

/** Lists the signed-in user's uploads for one conversation (Analyse chat id). */
export async function getConversationFiles(
  conversationId: string,
  clientId: string
): Promise<EvaluateConversationFile[]> {
  const response = await axiosInstance.get<EvaluateConversationFile[]>(
    `conversationFiles/${conversationId}`,
    { params: { clientId } }
  );

  const data = response.data ?? [];
  return data.map((file) =>
    normalizeEvaluateConversationFile(file as unknown as Record<string, unknown>)
  );
}

function normalizeEvaluateConversationFile(file: Record<string, unknown>): EvaluateConversationFile {
  return {
    fileId: String(file.fileId ?? file.FileId ?? ''),
    fileName: String(file.fileName ?? file.FileName ?? ''),
    extension: String(file.extension ?? file.Extension ?? ''),
    fileSize: Number(file.fileSize ?? file.FileSize ?? 0),
    conversationId: String(file.conversationId ?? file.ConversationId ?? ''),
    uploadedAt: String(file.uploadedAt ?? file.UploadedAt ?? ''),
    uploadedBy: String(file.uploadedBy ?? file.UploadedBy ?? ''),
  };
}

export async function getConversationFileUrl(
  clientId: string,
  conversationId: string,
  fileId: string,
  fileName: string
): Promise<string> {
  const response = await axiosInstance.get(
    `GetConversationFileUrl/${conversationId}/${fileId}`,
    { params: { clientId, fileName } }
  );
  return normalizeBlobUrl(response.data);
}

export interface SavedConversationFile {
  fileId: string;
  fileName: string;
  portfolioId: string;
  folderId: string;
  linkId: string;
}

/** Copies an inline upload into a portfolio folder, links it, and removes the upload. */
export async function saveConversationFileToLibrary(
  fileId: string,
  clientId: string,
  portfolioId: string,
  folderId: string
): Promise<SavedConversationFile> {
  const response = await axiosInstance.post<SavedConversationFile>(
    `conversationFile/${fileId}/saveToLibrary`,
    { portfolioId, folderId },
    { params: { clientId } }
  );
  const data = (response.data ?? {}) as unknown as Record<string, unknown>;
  return {
    fileId: String(data.fileId ?? data.FileId ?? ""),
    fileName: String(data.fileName ?? data.FileName ?? ""),
    portfolioId: String(data.portfolioId ?? data.PortfolioId ?? ""),
    folderId: String(data.folderId ?? data.FolderId ?? ""),
    linkId: String(data.linkId ?? data.LinkId ?? ""),
  };
}

export async function deleteConversationFile(fileId: string, clientId: string): Promise<void> {
  await axiosInstance.delete(`conversationFile/${fileId}`, { params: { clientId } });
}

/**
 * Uploads a file for the Analyse chat flow. Uses the chat id as the MCP conversation id.
 * Omit conversationId on the first upload; the server creates one and returns it.
 */
export async function uploadAnalyseConversationFile(
  file: File,
  clientId: string,
  conversationId?: string
): Promise<ConversationFileUploadResponse> {
  const formData = new FormData();
  formData.append("clientId", clientId);
  formData.append("file", file);

  const route = conversationId
    ? `uploadConversationFile/${conversationId}`
    : "uploadConversationFile";

  const response = await axiosInstance.post<ConversationFileUploadResponse>(route, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
}
